import type { ReactNode } from 'react';
import type { Table } from '@tanstack/react-table';
import type { SortingState, OnChangeFn } from '@tanstack/react-table';
import { ListFilter, Pencil, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ListViewColumnConfig } from '@/lib/views/listViewSchemas/types';
import { AdvancedSortList } from './AdvancedSortList';
import { AdvancedColumnVisibility } from './AdvancedColumnVisibility';
import { DensityToggle } from './DensityToggle';
import { DataGridKeyboardHelp } from '@/components/data-grid/DataGridKeyboardHelp';
import { DEFAULT_ADVANCED_TABLE_LABELS, type AdvancedTableLabels } from './advancedTableLabels';
import type { DataGridLabels } from '@/components/data-grid/dataGridLabels';

interface AdvancedTableToolbarProps<TData> {
  children?: ReactNode;
  filtersOpen?: boolean;
  onToggleFilters?: () => void;
  activeFilterCount?: number;
  // Sort
  columns?: ListViewColumnConfig[];
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  // Column visibility
  table?: Table<TData>;
  labels?: Partial<AdvancedTableLabels>;
  // Data grid edit mode
  isEditMode?: boolean;
  onToggleEditMode?: () => void;
  // Keyboard help
  isHelpOpen?: boolean;
  onHelpOpenChange?: (open: boolean) => void;
  dataGridLabels?: Partial<DataGridLabels>;
  // Focus mode
  isFocusMode?: boolean;
  onExitFocusMode?: () => void;
}

/**
 * Toolbar for the Advanced Table view.
 * Contains filter toggle, sort + columns buttons.
 */
export function AdvancedTableToolbar<TData>({
  children,
  filtersOpen,
  onToggleFilters,
  activeFilterCount = 0,
  columns,
  sorting,
  onSortingChange,
  table,
  labels,
  isEditMode,
  onToggleEditMode,
  isHelpOpen,
  onHelpOpenChange,
  dataGridLabels,
  isFocusMode,
  onExitFocusMode,
}: AdvancedTableToolbarProps<TData>) {
  const l = { ...DEFAULT_ADVANCED_TABLE_LABELS, ...labels };

  return (
    <div className="flex items-center gap-2 py-2" role="toolbar" aria-label="Table controls">
      {/* Filter toggle */}
      {onToggleFilters && (
        <Button
          variant={filtersOpen ? 'secondary' : 'outline'}
          size="sm"
          className={cn('h-8 gap-1.5 text-xs', filtersOpen && 'bg-accent')}
          onClick={onToggleFilters}
        >
          <ListFilter className="h-3.5 w-3.5" />
          {l.filters}
          {activeFilterCount > 0 && (
            <Badge
              variant="secondary"
              className="ml-0.5 h-5 min-w-[20px] rounded-full px-1.5 text-[10px] font-semibold"
            >
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      )}

      {/* Sort popover */}
      {columns && sorting && onSortingChange && (
        <AdvancedSortList
          columns={columns}
          sorting={sorting}
          onSortingChange={onSortingChange}
          labels={labels}
        />
      )}

      {/* Column visibility popover */}
      {table && <AdvancedColumnVisibility table={table} labels={labels} />}

      {/* Density toggle */}
      <DensityToggle labels={labels} />

      {/* Data grid edit toggle */}
      {onToggleEditMode && (
        <Button
          variant={isEditMode ? 'secondary' : 'outline'}
          size="sm"
          className={cn(
            'h-8 gap-1.5 text-xs',
            isEditMode && 'bg-neutral-200 text-neutral-800 ring-1 ring-neutral-300 dark:bg-neutral-700 dark:text-neutral-200 dark:ring-neutral-600',
          )}
          onClick={onToggleEditMode}
          aria-pressed={isEditMode}
          aria-label={dataGridLabels?.dgToggleEditMode ?? 'Toggle edit mode'}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      )}

      {/* Keyboard help (shown only in edit mode) */}
      {isEditMode && onHelpOpenChange && (
        <DataGridKeyboardHelp
          open={isHelpOpen ?? false}
          onOpenChange={onHelpOpenChange}
          labels={dataGridLabels}
        />
      )}

      {/* Exit Focus button (right-aligned, visible only in focus mode) */}
      {isFocusMode && onExitFocusMode && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 text-xs ml-auto"
          onClick={onExitFocusMode}
          aria-label={l.exitFocus}
        >
          <X className="h-3.5 w-3.5" />
          {l.exitFocus}
        </Button>
      )}

      {children}
    </div>
  );
}
